"use client";

import React, { useState } from "react";
import { Check, Sparkles, ShieldCheck, Zap, Globe } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const plans = [{
  id: "starter",
  name: "Starter",
  monthlyPrice: 75,
  aiCredits: "25",
  siteGenerations: "5",
  description: "Perfect for creators and solo entrepreneurs starting their journey.",
  benefits: ["AI Website Builder Access", "Managed WordPress Hosting", "Standard SSL Certificate", "Weekly Automated Backups", "Basic AI Content Tools"],
  isPopular: false,
  buttonText: "Start 7-Day Free Trial",
  link: "https://account.webshingle.com/sign-up"
}, {
  id: "growth",
  name: "Growth",
  monthlyPrice: 150,
  aiCredits: "75",
  siteGenerations: "10",
  description: "The ultimate solution for growing businesses and professional shops.",
  benefits: ["Everything in Starter", "Advanced AI Image Generation", "E-commerce Support", "Priority Support 24/7", "Daily Automated Backups"],
  isPopular: true,
  buttonText: "Start 7-Day Free Trial",
  link: "https://account.webshingle.com/sign-up"
}, {
  id: "premium",
  name: "Premium",
  monthlyPrice: 300,
  aiCredits: "200",
  siteGenerations: "25",
  description: "Scale your production with advanced AI tools and multi-site tools.",
  benefits: ["Everything in Growth", "Multi-site Management", "White-label Options", "Advanced SEO AI Tools", "Dedicated Account Manager"],
  isPopular: false,
  buttonText: "Start 7-Day Free Trial",
  link: "https://account.webshingle.com/sign-up"
}, {
  id: "elite",
  name: "Elite",
  monthlyPrice: 2600,
  aiCredits: "Unlimited",
  siteGenerations: "Unlimited",
  eliteNote: "*Subject to fair use policy and custom infrastructure requirements.",
  description: "Enterprise-grade performance for high-traffic organizations.",
  benefits: ["Everything in Premium", "Custom AI Model Training", "Unlimited Storage & Bandwidth", "99.9% Uptime SLA", "24/7 VIP Phone Support"],
  isPopular: false,
  buttonText: "Start 7-Day Free Trial",
  link: "https://account.webshingle.com/sign-up"
}];
export default function NosForfaitsPremium() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [showAnnual, setShowAnnual] = useState(false);
  return <section data-section-id="224297" id="choose-your-growth-path" ref={sectionRef} className="bg-background text-foreground py-24 px-4 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-lg opacity-50" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-primary/5 blur-[100px] rounded-lg opacity-30" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className={`text-center max-w-3xl mx-auto mb-12 transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-default text-4xl md:text-5xl font-bold mb-6 tracking-tight text-[#1F1F1F] dark:text-white">
            Choose Your <span className="text-[#A8D72E]">Growth Path</span>
          </h2>
          <p className="text-[#6B7280] text-lg font-default">
            Simple, transparent pricing for every stage of your journey. Build your professional WordPress presence with the power of AI and fully customizable subscriptions.
          </p>
        </div>

        <div className="flex justify-center items-center gap-4 mb-16">
          <span className={`text-sm font-medium ${!showAnnual ? "text-[#1F1F1F] dark:text-white" : "text-[#6B7280]"}`}>Monthly</span>
          <Button variant="ghost" size="sm" onClick={() => setShowAnnual(!showAnnual)} className="relative w-14 h-7 bg-muted hover:bg-muted rounded-lg p-1 transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-[#A8D72E] border-0">
            <div className={`w-5 h-5 bg-[#A8D72E] rounded-lg transition-transform duration-300 transform ${showAnnual ? "translate-x-7" : "translate-x-0"}`} />
          </Button>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${showAnnual ? "text-[#1F1F1F] dark:text-white" : "text-[#6B7280]"}`}>Annual</span>
            <Badge variant="outline" className="border-[#A8D72E] text-[#A8D72E] bg-[#A8D72E]/10 rounded-lg">Save 15%</Badge>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch max-w-7xl mx-auto">
          {plans.map((plan, i) => {
          const price = showAnnual ? Math.floor(plan.monthlyPrice * 12 * 0.85) : plan.monthlyPrice;
          const period = showAnnual ? "an" : "mo";
          return <div data-index={i} key={plan.id} className={`transition-all duration-700 delay-[${i * 100}ms] transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
                <Card className={`relative h-full flex flex-col bg-white dark:bg-[#1F1F1F] border-border transition-all duration-300 hover:shadow-lg rounded-lg ${plan.isPopular ? "border-2 border-[#A8D72E] shadow-xl lg:scale-105 z-20" : "z-10 hover:border-[#A8D72E]/30 shadow-md"}`}>
                  {plan.isPopular && <div className="absolute -top-4 left-0 right-0 flex justify-center z-30">
                      <Badge className="bg-[#A8D72E] text-[#1F1F1F] px-4 py-1 text-sm font-bold uppercase tracking-wider shadow-md flex items-center gap-1 rounded-lg border-0">
                        <Sparkles className="w-4 h-4" />
                        Most Popular
                      </Badge>
                    </div>}

                  {plan.id === "elite" && <div className="absolute -top-4 left-0 right-0 flex justify-center z-30">
                      <Badge className="bg-[#1F1F1F] text-white px-4 py-1 text-sm font-bold uppercase tracking-wider shadow-md flex items-center gap-1 rounded-lg border border-white/10">
                        <ShieldCheck className="w-4 h-4 text-[#A8D72E]" />
                        Custom Support
                      </Badge>
                    </div>}

                  <CardHeader className="text-center pb-2 pt-8">
                    <CardTitle className="font-default text-2xl text-[#1F1F1F] dark:text-white mb-2">
                      {plan.name}
                    </CardTitle>
                    <div className="flex justify-center items-baseline gap-1 mb-2">
                      <span className="text-2xl font-bold text-[#6B7280] self-start mt-1">$</span>
                      <span className="font-default text-5xl font-bold text-[#1F1F1F] dark:text-white">
                        {price}
                      </span>
                      <span className="text-[#6B7280] text-lg font-medium">
                        /{period}
                      </span>
                    </div>
                    <CardDescription className="text-sm font-default min-h-[40px] text-[#6B7280]">
                      {plan.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="flex-grow pt-6">
                    <div className="mb-6 space-y-3">
                      <div className="flex items-center gap-2 p-2 bg-accent/50 rounded-lg border border-border/50">
                        <Zap className="w-4 h-4 text-[#A8D72E]" />
                        <div className="flex flex-col">
                          <span className="text-[10px] uppercase font-bold text-[#6B7280] leading-none">AI Credits</span>
                          <span className="text-sm font-bold text-foreground">{plan.aiCredits} / mo</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-accent/50 rounded-lg border border-border/50">
                        <Globe className="w-4 h-4 text-[#A8D72E]" />
                        <div className="flex flex-col">
                          <span className="text-[10px] uppercase font-bold text-[#6B7280] leading-none">Site Generations</span>
                          <span className="text-sm font-bold text-foreground">{plan.siteGenerations} / mo</span>
                        </div>
                      </div>
                    </div>

                    <ul className="space-y-4">
                      {plan.benefits.map((benefit, index) => <li key={index} data-index={index} className="flex items-start gap-3 text-sm font-default">
                          <div className="mt-0.5 bg-[#A8D72E]/10 p-1 rounded-lg shrink-0">
                            <Check className="w-4 h-4 text-[#A8D72E]" />
                          </div>
                          <span className="text-foreground/90">{benefit}</span>
                        </li>)}
                    </ul>

                    {plan.eliteNote && <p className="text-[10px] text-[#6B7280] italic mt-6 leading-tight">
                        {plan.eliteNote}
                      </p>}
                  </CardContent>

                  <CardFooter className="pt-6 pb-8">
                    <Button asChild size="lg" className={`w-full font-bold text-base transition-all duration-300 rounded-lg ${plan.isPopular ? "bg-[#A8D72E] hover:bg-[#A8D72E]/90 text-[#1F1F1F] border-0 shadow-md relative overflow-hidden group" : "bg-white dark:bg-transparent hover:bg-accent text-[#1F1F1F] dark:text-white border border-border hover:border-[#A8D72E]/50"}`}>
                      <Link to={plan.link}>
                        {plan.isPopular && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />}
                        <span className="relative z-10">{plan.buttonText}</span>
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}